import { Link, NavLink, useLocation } from "react-router";
import { NewLink } from "@/components/new-link";
import { Button, buttonVariants } from "./button";
import { useGetMyAccountInfo } from "@/api/my-account-http-functions";
import {
    Devices,
    Help,
    Home,
    JcLogoLong,
    JcLogoLongDarkMode,
    JcLogoShort,
    JcLogoShortDarkMode,
    JcLogoLongDeprecated,
    JcLogoLongDarkModeDeprecated,
    JcLogoShortDeprecated,
    JcLogoShortDarkModeDeprecated,
    ManageCustomers,
    MyNetwork,
    Search,
    Settings,
    Sites,
    System,
    User,
    Fleet,
} from "@/assets";
import { AmplitudeEvent, FeatureFlag } from "@/constants";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/theme-provider";
import AccessGuard from "../access-guard";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "./dropdown-menu";
import { createMyAccountApiAsync } from "@/services/api-service";
import { useMsal } from "@azure/msal-react";
import { useError } from "@/contexts/error";
import { useIsCustomer } from "@/hooks/useIsCustomer";
import { track } from "@amplitude/analytics-browser";
import {
    filterAndSortPosts,
    useHelpPostManifest,
} from "@/pages/help/use-help-post";
import { Skeleton } from "./skeleton";
import { useIsStaff } from "@/hooks/useIsStaff";
import { AiChatbotDialog } from "@/components/ui/ai-chatbot-dialog";
import { useOnboardingTour } from "@/onboarding/onboarding-tour-context";
import { useFlags } from "launchdarkly-react-client-sdk";

const Navbar = () => {
    const location = useLocation();

    const isCustomer = useIsCustomer();

    const { outputTheme } = useTheme();

    const flags = useFlags();

    return (
        <nav className="gap-default border-subtle bg-elevation-surface-overlay-default px-wide py-default z-10 flex w-full items-center justify-between border-b pr-6">
            <div className="gap-default flex items-center">
                <NewLink
                    to={"/"}
                    className="gap-wide pr-default xl:gap-default inline-flex items-center"
                >
                    {flags[FeatureFlag.IL_4194_POWER_MOVE] ? (
                        outputTheme === "dark" ? (
                            <>
                                <JcLogoLongDarkMode className="hidden h-8 w-auto xl:block" />
                                <JcLogoShortDarkMode className="block h-8 w-auto xl:hidden" />
                            </>
                        ) : (
                            <>
                                <JcLogoLong className="hidden h-8 w-auto xl:block" />
                                <JcLogoShort className="block h-8 w-auto xl:hidden" />
                            </>
                        )
                    ) : outputTheme === "dark" ? (
                        <>
                            <JcLogoLongDarkModeDeprecated className="hidden xl:block" />
                            <JcLogoShortDarkModeDeprecated className="block xl:hidden" />
                        </>
                    ) : (
                        <>
                            <JcLogoLongDeprecated className="hidden xl:block" />
                            <JcLogoShortDeprecated className="block xl:hidden" />
                        </>
                    )}
                    <span className="text-large font-bolder text-default-color hidden xl:inline">
                        Illuminate
                    </span>
                </NewLink>
                <div
                    className="gap-default flex"
                    data-tour-step="landing-page-navigation"
                >
                    <div data-tour-step="landing-page-home">
                        <NavLink
                            title="Home"
                            to={"/"}
                            className={() =>
                                cn(
                                    buttonVariants({
                                        appearance: "navigation",
                                        spacing: "default",
                                        isSelected: location.pathname === "/",
                                    }),
                                    "gap-narrow flex",
                                )
                            }
                        >
                            <Home />
                            <span className="hidden font-bold lg:inline">
                                Home
                            </span>
                        </NavLink>
                    </div>
                    <NavLink
                        title="My network"
                        to={"/network"}
                        className={({ isActive }) =>
                            cn(
                                buttonVariants({
                                    appearance: "navigation",
                                    spacing: "default",
                                    isSelected: isActive,
                                }),
                                "gap-narrow flex",
                            )
                        }
                        data-tour-step="my-network-navigation"
                    >
                        <MyNetwork />
                        <span className="hidden font-bold lg:inline">
                            My network
                        </span>
                    </NavLink>
                    <AccessGuard
                        permissions={[
                            "device_group_create",
                            "partner_organisation_manage",
                            "system_device_provisioning",
                            "partner_device_provisioning",
                        ]}
                        permissionsSelector="any"
                        unauthorizedBehaviour={"hide"}
                        overrideAndRestrict={isCustomer}
                    >
                        <NavLink
                            title="Manage customers"
                            to={"/manage-customers"}
                            className={({ isActive }) =>
                                cn(
                                    buttonVariants({
                                        appearance: "navigation",
                                        spacing: "default",
                                        isSelected: isActive,
                                    }),
                                    "gap-narrow flex",
                                )
                            }
                        >
                            <ManageCustomers />
                            <span className="hidden font-bold lg:inline">
                                Manage customers
                            </span>
                        </NavLink>
                    </AccessGuard>
                    <AccessGuard
                        unauthorizedBehaviour={"hide"}
                        overrideAndRestrict={!isCustomer}
                    >
                        <NavLink
                            title="Sites"
                            to={"/sites"}
                            className={({ isActive }) =>
                                cn(
                                    buttonVariants({
                                        appearance: "navigation",
                                        spacing: "default",
                                        isSelected: isActive,
                                    }),
                                    "gap-narrow flex",
                                )
                            }
                        >
                            <Sites />
                            <span className="hidden font-bold lg:inline">
                                Sites
                            </span>
                        </NavLink>
                        <NavLink
                            title="Devices"
                            to={"/devices"}
                            className={({ isActive }) =>
                                cn(
                                    buttonVariants({
                                        appearance: "navigation",
                                        spacing: "default",
                                        isSelected: isActive,
                                    }),
                                    "gap-narrow flex",
                                )
                            }
                        >
                            <Devices />
                            <span className="hidden font-bold lg:inline">
                                Devices
                            </span>
                        </NavLink>
                    </AccessGuard>
                    <AccessGuard
                        featureFlags={[FeatureFlag.IL_3918_VEHICLE_MANAGEMENT]}
                        unauthorizedBehaviour="hide"
                    >
                        <NavLink
                            title="Manage fleet"
                            to={"/manage-fleet"}
                            className={({ isActive }) =>
                                cn(
                                    buttonVariants({
                                        appearance: "navigation",
                                        spacing: "default",
                                        isSelected: isActive,
                                    }),
                                    "gap-narrow flex",
                                )
                            }
                        >
                            <Fleet />
                            <span className="hidden font-bold lg:inline">
                                Manage fleet
                            </span>
                        </NavLink>
                    </AccessGuard>
                    <AccessGuard
                        accessTags={["IlluminateAdmin"]}
                        unauthorizedBehaviour={"hide"}
                    >
                        <NavLink
                            title="System"
                            to={"/system"}
                            className={({ isActive }) =>
                                cn(
                                    buttonVariants({
                                        appearance: "navigation",
                                        spacing: "default",
                                        isSelected: isActive,
                                    }),
                                    "gap-narrow flex",
                                )
                            }
                        >
                            <System />
                            <span className="hidden font-bold lg:inline">
                                System
                            </span>
                        </NavLink>
                    </AccessGuard>
                </div>
            </div>
            <div className="gap-default flex items-center">
                <AccessGuard
                    featureFlags={[FeatureFlag.AI_CHATBOT_HELPER]}
                    unauthorizedBehaviour="hide"
                >
                    <AiChatbotDialog />
                </AccessGuard>
                <NavLink
                    title="Search"
                    to={"/search"}
                    className={({ isActive }) =>
                        cn(
                            buttonVariants({
                                appearance: "navigation",
                                spacing: "default",
                                isSelected: isActive,
                            }),
                            "gap-narrow flex",
                        )
                    }
                    onClick={() => track(AmplitudeEvent.GLOBAL_SEARCH_CLICKED)}
                >
                    <Search />
                </NavLink>
                {!flags[FeatureFlag.IL_4090_ONBOARDING_TUTORIAL_CUSTOMER] ? (
                    <NavLink
                        title="Help"
                        to={"/help"}
                        className={({ isActive }) =>
                            cn(
                                buttonVariants({
                                    appearance: "navigation",
                                    spacing: "default",
                                    isSelected: isActive,
                                }),
                                "gap-narrow flex",
                            )
                        }
                        onClick={() =>
                            track(AmplitudeEvent.GLOBAL_HELP_CLICKED)
                        }
                    >
                        <Help />
                    </NavLink>
                ) : (
                    <HelpDropdownMenu />
                )}
                <NavLink
                    data-tour-step="settings-navigation"
                    title="Settings"
                    to={"/settings"}
                    className={({ isActive }) =>
                        cn(
                            buttonVariants({
                                appearance: "navigation",
                                spacing: "default",
                                isSelected: isActive,
                            }),
                            "gap-narrow flex",
                        )
                    }
                    onClick={() =>
                        track(AmplitudeEvent.GLOBAL_SETTINGS_CLICKED)
                    }
                >
                    <Settings />
                </NavLink>
                <AccountDropdownMenu />
            </div>
        </nav>
    );
};

export const AccountDropdownMenu = () => {
    const { instance } = useMsal();
    const errorContext = useError();

    const { data: accountInfo } = useGetMyAccountInfo();
    const { theme, setTheme } = useTheme();
    const onLogoutClick = async () => {
        const api = await createMyAccountApiAsync(errorContext, instance);
        await api.logOut();

        await instance.logoutRedirect();
    };
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button appearance="navigation" title="Account Dropdown">
                    <User />
                    {accountInfo?.name && (
                        <span className="hidden font-bold xl:inline">
                            {`${accountInfo.name} - ${accountInfo.organisationId}`}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuSub>
                    <DropdownMenuSubTrigger>Theme</DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                            <DropdownMenuItem
                                data-selected={theme === "light"}
                                onClick={() => setTheme("light")}
                            >
                                Light
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-selected={theme === "dark"}
                                onClick={() => setTheme("dark")}
                            >
                                Dark
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-selected={theme === "system"}
                                onClick={() => setTheme("system")}
                            >
                                Match system
                            </DropdownMenuItem>
                        </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <WhatsNewDropdownItem />

                <DropdownMenuItem title="Log out" onClick={onLogoutClick}>
                    Log out
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const HelpDropdownMenu = () => {
    const { isAvailable, startTour } = useOnboardingTour();
    const onTakeTour = () => {
        startTour();
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    appearance="navigation"
                    spacing="default"
                    title="Help"
                    aria-label="Help"
                    className="gap-narrow flex"
                >
                    <Help />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuItem title="Browse all help" asChild>
                    <Link
                        to="/help"
                        onClick={() =>
                            track(AmplitudeEvent.GLOBAL_HELP_CLICKED)
                        }
                    >
                        Browse all help
                    </Link>
                </DropdownMenuItem>
                {isAvailable && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            title="Take the tour"
                            onClick={() => {
                                track(AmplitudeEvent.GLOBAL_HELP_CLICKED);
                                onTakeTour();
                            }}
                        >
                            Take the tour
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const WhatsNewDropdownItem = () => {
    const { postManifest, isLoading } = useHelpPostManifest({ enabled: true });
    const isStaff = useIsStaff();
    const latestPost = filterAndSortPosts(
        postManifest,
        isStaff,
        "whats-new",
    ).at(0);

    if (isLoading) {
        return (
            <DropdownMenuItem title="What's new">
                <Skeleton className="h-8 w-24" />
            </DropdownMenuItem>
        );
    }

    if (!latestPost) return null;

    return (
        <DropdownMenuItem title="What's new" asChild>
            <Link to={`/help/${latestPost?.slug}`}>What's new</Link>
        </DropdownMenuItem>
    );
};

export default Navbar;
